import React, { forwardRef, ReactNode } from "react";
import { Box, Tooltip, styled } from "@mui/material";
import { CircularProgress } from "../../../../../components";
import { colors } from "../../config";

interface ToggleButtonProps {
  children: ReactNode;
  toggled: boolean;
  onToggle?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  tooltip?: string;
  disabled?: boolean;
  loading?: boolean;
}

interface StyledButtonProps {
  toggled: boolean;
  disabled?: boolean;
}

const StyledButton = styled("button")<StyledButtonProps>(
  ({ toggled, disabled }) => ({
    width: "25px",
    height: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: toggled ? colors.toggle.activeBackground : colors.toggle.inactiveBackground,
    border: toggled ? colors.toggle.activeBorder : colors.toggle.inactiveBorder,
    borderRadius: "3.6px",
    padding: 0,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
    boxShadow: "none",
    position: "relative",
    overflow: "hidden",

    "& svg": {
      fill: "currentColor",
      color: "inherit",
      position: "relative",
      zIndex: 1,
      fontSize: "16px",
    },

    "& svg path": {
      fill: "currentColor",
    },

    color: toggled ? colors.toggle.activeText : colors.toggle.inactiveText, // Dark teal when toggled, light grey when off

    "&:hover": {
      background: disabled
        ? toggled
          ? colors.toggle.activeBackground
          : colors.toggle.inactiveBackground
        : toggled
          ? colors.toggle.hoverActive
          : colors.toggle.hoverInactive,
    },

    "&:active": {
      background: disabled
        ? toggled
          ? colors.toggle.activeBackground
          : colors.toggle.inactiveBackground
        : toggled
          ? colors.toggle.pressedActive
          : colors.toggle.pressedInactive,
    },
  }),
);

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    { children, toggled, onToggle, tooltip, disabled = false, loading },
    ref,
  ) => {
    const button = (
      <StyledButton
        ref={ref}
        onClick={onToggle}
        disabled={disabled}
        toggled={toggled}
      >
        {loading ? <CircularProgress size="sm" color="inherit" /> : children}
      </StyledButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow>
          <Box component="span" sx={{ display: "inline-flex" }}>
            {button}
          </Box>
        </Tooltip>
      );
    }

    return button;
  },
);

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
